import { useState, useEffect, useRef } from "react";
import { collection, query, where, orderBy, limit, startAfter, onSnapshot, doc, setDoc, updateDoc, getDocs, getDoc, arrayUnion, Timestamp } from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { db } from "../../firebase.js";
import { useAuth } from "../../useAuth.js";
import "./admin.css";

/** Toggle keys per Jacket/Pants/Shirt/Vest section (matches admin alteration buttons / reports). */
const SECTION_ALTERATION_KEYS = {
  Jacket: [
    'sleeveLonger',
    'sleeveShorter',
    'takeIn',
    'takeOut',
    'shorterJacket',
    'shoulderPads',
    'functionButtonHole',
    'changeButtonsJacket',
    'taperSleeve',
    'shattnezTest',
    'removeShattnez'
  ],
  Pants: [
    'hemming',
    'taperPants',
    'waistIn',
    'waistOut',
    'seatIn',
    'seatOut',
    'crotchIn',
    'crotchOut',
    'crotchLining',
    'changeZipper',
    'length',
    'changeButtons',
    'suspenderButtons'
  ],
  Shirt: ['shorterSleeve', 'takeInShirt', 'takeOutShirt', 'iron'],
  Vest: ['changeButtonsVest', 'takeInVest', 'takeOutVest']
};

function countSelectedAlterationsInSection(alterationDetails, section) {
  const keys = SECTION_ALTERATION_KEYS[section];
  if (!keys?.length || !alterationDetails) return 0;
  return keys.filter((key) => {
    const v = alterationDetails[key];
    return (
      v === true ||
      (typeof v === 'string' && v.trim() !== '') ||
      (typeof v === 'number' && !Number.isNaN(v))
    );
  }).length;
}

function normalizeUsPhoneDigits(raw) {
  if (raw == null) return '';
  let d = String(raw).replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  return d;
}

const PAGE_SIZE = 10;
const PAGES_PER_BATCH = 10;
const BATCH_SIZE = PAGE_SIZE * PAGES_PER_BATCH; // 100 orders = 10 pages
const SEARCH_SCAN_SIZE = 400;

function mapOrderDoc(d) {
  return { id: d.id, ...d.data() };
}

function getOrderTimeMs(order) {
  const t = order?.timestamp ?? order?.createdAt ?? order?.shopifyUpdatedAt;
  if (t && typeof t.toDate === 'function') {
    const ms = t.toDate().getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }
  if (t && typeof t.seconds === 'number') {
    return t.seconds * 1000 + Math.floor((t.nanoseconds || 0) / 1e6);
  }
  if (t && typeof t._seconds === 'number') {
    return t._seconds * 1000;
  }
  if (typeof t === 'number' && Number.isFinite(t)) {
    return t < 1e12 ? t * 1000 : t;
  }
  if (t) {
    const ms = new Date(t).getTime();
    if (!Number.isNaN(ms)) return ms;
  }
  return 0;
}

function getOrderNumber(order) {
  const n = parseInt(String(order?.id || '').replace(/\D/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
}

function sortOrdersByTimestampDesc(orders) {
  return [...orders].sort((a, b) => {
    const timeDiff = getOrderTimeMs(b) - getOrderTimeMs(a);
    if (timeDiff !== 0) return timeDiff;
    return getOrderNumber(b) - getOrderNumber(a);
  });
}

const TIMESTAMP_EPOCH = Timestamp.fromMillis(0);

function newestOrdersQuery(ordersRef, { cursor, pageSize } = {}) {
  const constraints = [
    where('timestamp', '>=', TIMESTAMP_EPOCH),
    orderBy('timestamp', 'desc'),
  ];
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(pageSize || BATCH_SIZE));
  return query(ordersRef, ...constraints);
}

function doesOrderMatchSearch(order, term) {
  const searchLower = String(term || '')
    .toLowerCase()
    .trim()
    .replace(/^r:\s*/i, '');
  if (!searchLower) return true;
  return (
    String(order.id || '').toLowerCase().includes(searchLower) ||
    String(order.receiptNumber || '').toLowerCase().includes(searchLower) ||
    String(order.name || '').toLowerCase().includes(searchLower) ||
    String(order.barcodeId || '').toLowerCase().includes(searchLower)
  );
}

function Admin() {
  const { user, loading: authLoading, claims } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastOrderUrlParamRef = useRef(null);
  const [, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreOrders, setHasMoreOrders] = useState(true);
  const [hasPreviousOrders, setHasPreviousOrders] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [originalOrder, setOriginalOrder] = useState(null); // Store original database state for comparison
  const [showModal, setShowModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [expandedSections, setExpandedSections] = useState({}); // per-item per-section (Jacket/Pants/Shirt/Vest)
  const [statusLogExpanded, setStatusLogExpanded] = useState(false);
  const [showAddCustomerPanel, setShowAddCustomerPanel] = useState(false);
  const [addCustomerNameDraft, setAddCustomerNameDraft] = useState('');
  const [addCustomerPhoneDraft, setAddCustomerPhoneDraft] = useState('');
  const addCustomerPhoneLookupSeq = useRef(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchingAll, setSearchingAll] = useState(false);
  const extraLoadedRef = useRef(false);
  const nextCursorRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const searchGenerationRef = useRef(0);
  const allOrdersRef = useRef([]);
  allOrdersRef.current = allOrders;

  // Live listener for the newest 10 pages (100 orders). Older pages load on demand.
  useEffect(() => {
    if (!user || authLoading) {
      return;
    }

    extraLoadedRef.current = false;
    nextCursorRef.current = null;
    setAllOrders([]);
    setCurrentPage(0);

    const ordersRef = collection(db, "Orders");
    const q = newestOrdersQuery(ordersRef);

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      try {
        const firstBatch = sortOrdersByTimestampDesc(querySnapshot.docs.map(mapOrderDoc));

        setAllOrders((prev) => {
          if (!extraLoadedRef.current) {
            return firstBatch;
          }
          const byId = new Map(prev.map((o) => [o.id, o]));
          firstBatch.forEach((o) => byId.set(o.id, o));
          return sortOrdersByTimestampDesc(Array.from(byId.values()));
        });

        if (!extraLoadedRef.current) {
          nextCursorRef.current = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
          setHasMoreOrders(querySnapshot.size === BATCH_SIZE);
        }

        setOrders(firstBatch.slice(0, PAGE_SIZE));
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error("Error processing orders:", err);
        setError("Failed to process orders. Please refresh the page.");
        setLoading(false);
      }
    }, (err) => {
      console.error("Error listening to orders:", err);
      setError("Failed to connect to orders. Please check your connection.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, authLoading]);

  const loadMoreOrders = async () => {
    if (loadingMoreRef.current || !nextCursorRef.current) return [];
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const ordersRef = collection(db, "Orders");
      const q = newestOrdersQuery(ordersRef, { cursor: nextCursorRef.current });
      const snapshot = await getDocs(q);
      extraLoadedRef.current = true;
      nextCursorRef.current = snapshot.docs[snapshot.docs.length - 1] || nextCursorRef.current;
      setHasMoreOrders(snapshot.size === BATCH_SIZE);

      const nextBatch = snapshot.docs.map(mapOrderDoc);
      setAllOrders((prev) => {
        const byId = new Map(prev.map((o) => [o.id, o]));
        nextBatch.forEach((o) => {
          if (!byId.has(o.id)) byId.set(o.id, o);
        });
        return sortOrdersByTimestampDesc(Array.from(byId.values()));
      });
      return nextBatch;
    } catch (err) {
      console.error("Error loading more orders:", err);
      return [];
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  // Debounced full-catalog search: scan every Orders doc for name / receipt / ID / barcode
  useEffect(() => {
    const term = searchTerm.trim();
    if (!term) {
      searchGenerationRef.current += 1;
      setSearchResults([]);
      setSearchingAll(false);
      return;
    }

    setSearchingAll(true);
    const generationOnType = searchGenerationRef.current;
    const timeoutId = setTimeout(() => {
      const generation = ++searchGenerationRef.current;
      if (generationOnType >= generation) return;

      (async () => {
        const matches = [];
        const seen = new Set();
        const addMatch = (order) => {
          if (!order?.id || seen.has(order.id) || !doesOrderMatchSearch(order, term)) return;
          seen.add(order.id);
          matches.push(order);
        };

        allOrdersRef.current.forEach(addMatch);
        if (searchGenerationRef.current !== generation) return;
        setSearchResults(sortOrdersByTimestampDesc(matches));

        const idCandidates = [term];
        if (!term.startsWith('#')) idCandidates.push(`#${term}`);
        else idCandidates.push(term.slice(1));
        for (const id of idCandidates) {
          if (searchGenerationRef.current !== generation) return;
          try {
            const snap = await getDoc(doc(db, 'Orders', id));
            if (snap.exists()) addMatch(mapOrderDoc(snap));
          } catch (e) {
            console.warn('Exact order ID lookup failed:', e);
          }
        }

        const barcodeTerm = term.replace(/^R:\s*/i, '').trim();
        if (barcodeTerm && searchGenerationRef.current === generation) {
          try {
            const barcodeSnap = await getDocs(
              query(collection(db, 'Orders'), where('barcodeId', '==', barcodeTerm))
            );
            barcodeSnap.docs.forEach((d) => addMatch(mapOrderDoc(d)));
          } catch (e) {
            console.warn('Barcode ID lookup failed:', e);
          }
        }
        if (searchGenerationRef.current !== generation) return;
        setSearchResults(sortOrdersByTimestampDesc(matches));

        try {
          const ordersRef = collection(db, 'Orders');
          let cursor = null;
          while (searchGenerationRef.current === generation) {
            const q = cursor
              ? query(ordersRef, orderBy('timestamp', 'desc'), startAfter(cursor), limit(SEARCH_SCAN_SIZE))
              : query(ordersRef, orderBy('timestamp', 'desc'), limit(SEARCH_SCAN_SIZE));
            const snapshot = await getDocs(q);
            if (searchGenerationRef.current !== generation) return;
            snapshot.docs.forEach((d) => addMatch(mapOrderDoc(d)));
            setSearchResults(sortOrdersByTimestampDesc(matches));
            if (snapshot.size < SEARCH_SCAN_SIZE) break;
            cursor = snapshot.docs[snapshot.docs.length - 1];
          }
        } catch (e) {
          console.error('Full order search failed:', e);
        }

        if (searchGenerationRef.current === generation) {
          setSearchingAll(false);
        }
      })();
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Show 10 cards at a time (browse loaded pages, or full-catalog search results)
  useEffect(() => {
    const matching = searchTerm.trim() ? searchResults : allOrders;
    const startIndex = currentPage * PAGE_SIZE;
    setFilteredOrders(matching.slice(startIndex, startIndex + PAGE_SIZE));
    setHasPreviousOrders(currentPage > 0);
  }, [searchTerm, allOrders, searchResults, currentPage]);

  // Keep the open order modal in sync with Firestore (Shopify webhook updates phone/name/items, etc.)
  useEffect(() => {
    if (!showModal) return;

    setSelectedOrder((prev) => {
      if (!prev?.id) return prev;
      const live = allOrders.find((o) => o.id === prev.id);
      if (!live) return prev;

      const hasPendingField = (field) => Boolean(pendingChanges[`${prev.id}_${field}`]);
      const hasPendingItemEdits = Object.keys(pendingChanges).some((key) => {
        const c = pendingChanges[key];
        return c?.orderId === prev.id && (c?.itemIndex != null || String(key).includes('_alteration'));
      });

      const patch = {};
      if (!hasPendingField('phone') && (prev.phone || '') !== (live.phone || '')) {
        patch.phone = live.phone || '';
      }
      if (!hasPendingField('name') && (prev.name || '') !== (live.name || '')) {
        patch.name = live.name || '';
      }
      if ((prev.receiptNumber || '') !== (live.receiptNumber || '')) {
        patch.receiptNumber = live.receiptNumber || '';
      }
      if ((prev.email || '') !== (live.email || '')) {
        patch.email = live.email || '';
      }

      if (!hasPendingItemEdits) {
        const sig = (items) =>
          (Array.isArray(items) ? items : [])
            .map((i) => `${i?.shopifyLineItemId || ''}|${i?.name || ''}|${i?.quantity ?? ''}|${i?.needsAlteration ? 1 : 0}`)
            .join(';');
        if (sig(prev.line_items) !== sig(live.line_items)) {
          patch.line_items = live.line_items || [];
        }
      }

      if (Object.keys(patch).length === 0) return prev;
      return { ...prev, ...patch };
    });

    setOriginalOrder((prev) => {
      if (!prev?.id) return prev;
      const live = allOrders.find((o) => o.id === prev.id);
      if (!live) return prev;

      const hasPendingItemEdits = Object.keys(pendingChanges).some((key) => {
        const c = pendingChanges[key];
        return c?.orderId === prev.id && c?.itemIndex != null;
      });

      const patch = {
        phone: live.phone || '',
        name: live.name || '',
        receiptNumber: live.receiptNumber || '',
        email: live.email || '',
      };
      if (!hasPendingItemEdits) {
        patch.line_items = live.line_items || [];
      }
      const same =
        (prev.phone || '') === (patch.phone || '') &&
        (prev.name || '') === (patch.name || '') &&
        (prev.receiptNumber || '') === (patch.receiptNumber || '') &&
        (prev.email || '') === (patch.email || '') &&
        (hasPendingItemEdits || prev.line_items === patch.line_items);
      if (same) return prev;
      return { ...prev, ...patch };
    });
  }, [allOrders, showModal, pendingChanges]);

  const refreshOrders = () => {
    setLoading(true);
    setError(null);
  };

  const testFetchOrders = async () => {
    try {
      console.log("🧪 Testing manual fetch of orders...");
      console.log("🧪 Current user:", user?.uid, user?.email);
      console.log("🧪 Auth state:", { user: !!user, authLoading });
      
      const ordersRef = collection(db, "Orders");
      console.log("🧪 Orders collection reference created");
      
      const snapshot = await getDocs(ordersRef);
      console.log("🧪 Manual fetch result:", snapshot.size, "documents");
      
      if (snapshot.empty) {
        console.log("🧪 No orders found in collection");
      } else {
        snapshot.forEach((doc) => {
          console.log("🧪 Manual fetch - Order:", doc.id, doc.data());
        });
      }
    } catch (error) {
      console.error("🧪 Manual fetch error:", error);
      console.error("🧪 Error details:", {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
    }
  };

  const testCollectionAccess = async () => {
    try {
      console.log("🔍 Testing collection access...");
      
      const ordersRef = collection(db, "Orders");
      console.log("✅ Orders collection reference created");
      
      const testQuery = query(ordersRef, limit(1));
      const testSnapshot = await getDocs(testQuery);
      
      console.log("🔍 Collection access test result:", {
        canAccess: true,
        documentCount: testSnapshot.size,
        hasPermission: true
      });
      
    } catch (error) {
      console.error("🔍 Collection access test failed:", error);
      console.error("🔍 Error details:", {
        code: error.code,
        message: error.message,
        permissionDenied: error.code === 'permission-denied'
      });
    }
  };

  const isSearching = Boolean(searchTerm.trim());
  const matchingOrders = isSearching ? searchResults : allOrders;
  const loadedPages = Math.max(1, Math.ceil(matchingOrders.length / PAGE_SIZE) || 1);
  const canGoNextPage = isSearching
    ? currentPage < loadedPages - 1
    : currentPage < loadedPages - 1 || hasMoreOrders;

  const goToNextPage = async () => {
    if (currentPage < loadedPages - 1) {
      setCurrentPage((prev) => prev + 1);
      return;
    }
    if (isSearching || !hasMoreOrders || loadingMore) return;
    const added = await loadMoreOrders();
    const combinedCount = allOrders.length + added.filter((o) => !allOrders.some((p) => p.id === o.id)).length;
    const nextLoadedPages = Math.max(1, Math.ceil(combinedCount / PAGE_SIZE) || 1);
    if (currentPage < nextLoadedPages - 1) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const goToFirstPage = () => {
    setCurrentPage(0);
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'N/A';

    // phome regex
    const cleaned = phoneNumber.replace(/\D/g, '');

  
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 7) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    } else {
      return cleaned;
    }
  };

  /** True when customer name or phone is missing / placeholder (show "Add customer" in order modal). */
  const orderNeedsCustomerData = (order) => {
    if (!order) return false;
    const rawName = (order.name ?? '').toString().trim();
    const nameLower = rawName.toLowerCase();
    const badName =
      !rawName ||
      nameLower === 'undefined undefined' ||
      nameLower === 'undefined' ||
      /^undefined(\s+undefined)?$/i.test(rawName);
    const phoneStr = (order.phone ?? '').toString().trim();
    const digits = phoneStr.replace(/\D/g, '');
    const badPhone =
      !phoneStr ||
      phoneStr.toUpperCase() === 'N/A' ||
      digits.length < 10;
    return badName || badPhone;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else {
      date = new Date(timestamp);
    }

    const localTime = date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/New_York'
    });
    
    return localTime;
  };

  const formatDateOnly = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'America/New_York'
    });
  };

  const formatStatusLabel = (status) => {
    if (!status) return 'Unknown Status';
    return status
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatSmsStatus = (smsStatus) => {
    if (!smsStatus) return '';
    return String(smsStatus)
      .split(/[\s_]+/)
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatStatusLogTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (typeof timestamp === 'object' && typeof timestamp.seconds === 'number') {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }

    if (Number.isNaN(date.getTime())) {
      return 'Unknown time';
    }

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAlterationToggle = async (orderId, itemIndex, currentStatus) => {
    try {
      if (!selectedOrder.line_items || !Array.isArray(selectedOrder.line_items)) {
        console.error("Line items is not an array:", selectedOrder.line_items);
        alert("Cannot update alteration status: line items not found");
        return;
      }

      const newStatus = !currentStatus;

      // Get original value from database
      const originalItem = originalOrder?.line_items?.[itemIndex];
      const originalNeedsAlteration = originalItem?.needsAlteration || false;

      // Update local state first
      setSelectedOrder(prev => ({
        ...prev,
        line_items: prev.line_items.map((item, index) =>
          index === itemIndex
            ? {
              ...item,
              needsAlteration: newStatus,
              // If unselecting alteration, clear alterationDetails
              alterationDetails: newStatus ? (item.alterationDetails || {}) : {}
            }
            : item
        )
      }));

      // Compare against original database value
      if (newStatus === originalNeedsAlteration) {
        // No change from original - remove from pending changes
        setPendingChanges(prev => {
          const newPending = { ...prev };
          delete newPending[`${orderId}_${itemIndex}_needsAlteration`];
          // Also remove any alterationDetails changes for this item if unselecting
          if (!newStatus) {
            Object.keys(newPending).forEach(key => {
              if (key.startsWith(`${orderId}_${itemIndex}_alterationDetails.`)) {
                delete newPending[key];
              }
            });
          }
          return newPending;
        });
      } else {
        // Different from original - add to pending changes
        setPendingChanges(prev => ({
          ...prev,
          [`${orderId}_${itemIndex}_needsAlteration`]: {
            orderId,
            itemIndex,
            field: 'needsAlteration',
            value: newStatus
          }
        }));

        // If unselecting, also clear alterationDetails in pending changes
        if (!newStatus) {
          setPendingChanges(prev => {
            const newPending = { ...prev };
            Object.keys(newPending).forEach(key => {
              if (key.startsWith(`${orderId}_${itemIndex}_alterationDetails.`)) {
                delete newPending[key];
              }
            });
            return newPending;
          });
        }
      }

    } catch (error) {
      console.error("Error updating alteration status:", error);
      alert("Failed to update alteration status. Please try again.");
    }
  };

  const toggleAlterationDetails = (itemIndex) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemIndex]: !prev[itemIndex]
    }));
  };

  const toggleAlterationSection = (itemIndex, sectionKey) => {
    const key = `${itemIndex}-${sectionKey}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isAlterationSectionExpanded = (itemIndex, sectionKey) => {
    const key = `${itemIndex}-${sectionKey}`;
    return !!expandedSections[key];
  };

  const updateAlterationDetail = (itemIndex, field, value) => {
    // Get original value from database
    const originalItem = originalOrder?.line_items?.[itemIndex];
    const originalAlterationDetails = originalItem?.alterationDetails || {};
    const originalValue = originalAlterationDetails[field];

    // Update local state first
    setSelectedOrder(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, index) =>
        index === itemIndex
          ? {
            ...item,
            alterationDetails: {
              ...item.alterationDetails,
              [field]: value
            }
          }
          : item
      )
    }));

    // Compare against original database value
    const changeKey = `${selectedOrder.id}_${itemIndex}_${field}`;

    // Handle pending changes based on value type and content
    if (typeof value === 'boolean') {
      // For boolean values (toggle buttons)
      const originalBoolValue = originalValue === true;
      
      if (value === originalBoolValue) {
        // Same as original - remove from pending changes
        setPendingChanges(prev => {
          const newPending = { ...prev };
          delete newPending[changeKey];
          return newPending;
        });
      } else {
        // Different from original - add to pending changes
        setPendingChanges(prev => ({
          ...prev,
          [changeKey]: {
            orderId: selectedOrder.id,
            itemIndex,
            field: `alterationDetails.${field}`, 
            value
          }
        }));
      }
    } else if (typeof value === 'string') {
      // For string values (dueDate, priority, comments, numeric measurements)
      const trimmedValue = value.trim();
      const originalTrimmedValue = (originalValue || '').toString().trim();
      
      // Special handling for priority field - "none" should not be considered a change if original was also "none"
      if (field === 'priority') {
        if (trimmedValue === 'none' && (originalTrimmedValue === 'none' || !originalValue)) {
          // Remove from pending changes when priority is "none" and original was also "none"
          setPendingChanges(prev => {
            const newPending = { ...prev };
            delete newPending[changeKey];
            return newPending;
          });
        } else if (trimmedValue !== originalTrimmedValue) {
          // Different from original - add to pending changes
          setPendingChanges(prev => ({
            ...prev,
            [changeKey]: {
              orderId: selectedOrder.id,
              itemIndex,
              field: `alterationDetails.${field}`, 
              value: trimmedValue
            }
          }));
        } else {
          // Same as original - remove from pending changes
          setPendingChanges(prev => {
            const newPending = { ...prev };
            delete newPending[changeKey];
            return newPending;
          });
        }
      } else {
        // For other string fields (measurements, comments, etc.)
        if (trimmedValue === originalTrimmedValue) {
          // Same as original - remove from pending changes
          setPendingChanges(prev => {
            const newPending = { ...prev };
            delete newPending[changeKey];
            return newPending;
          });
        } else if (trimmedValue !== '') {
          // Different from original and not empty - add to pending changes
          setPendingChanges(prev => ({
            ...prev,
            [changeKey]: {
              orderId: selectedOrder.id,
              itemIndex,
              field: `alterationDetails.${field}`, 
              value: trimmedValue
            }
          }));
        } else {
          // Empty value - check if original was also empty
          if (originalTrimmedValue === '' || !originalValue) {
            // Both empty - remove from pending changes
            setPendingChanges(prev => {
              const newPending = { ...prev };
              delete newPending[changeKey];
              return newPending;
            });
          } else {
            // Original had a value, now empty - this is a change (clearing the field)
            setPendingChanges(prev => ({
              ...prev,
              [changeKey]: {
                orderId: selectedOrder.id,
                itemIndex,
                field: `alterationDetails.${field}`, 
                value: ''
              }
            }));
          }
        }
      }
    }
  };

  const saveAlterationChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      alert("No changes to save");
      return;
    }

    setSaving(true);

    try {
      const changesByOrder = {};
      const orderLevelChanges = {};

      // Search results may include orders not present in the paginated allOrders list
      const resolveOrderForSave = async (orderId) => {
        if (selectedOrder?.id === orderId) return selectedOrder;
        if (originalOrder?.id === orderId) return originalOrder;
        const fromSearch = searchResults.find((order) => order.id === orderId);
        if (fromSearch) return fromSearch;
        const fromAll = allOrders.find((order) => order.id === orderId);
        if (fromAll) return fromAll;
        try {
          const snap = await getDoc(doc(db, "Orders", orderId));
          if (snap.exists()) return { id: snap.id, ...snap.data() };
        } catch (e) {
          console.warn("Failed to load order for save:", orderId, e);
        }
        return null;
      };

      Object.values(pendingChanges).forEach(change => {
        const { orderId, itemIndex, field, value, newStatus } = change;

        // Handle order-level fields (like smsSubscribed)
        if (itemIndex === 'order' && (field === 'smsSubscribed' || field === 'status' || field === 'priority' || field === 'assignedUser' || field === 'dueDate' || field === 'dueTime' || field === 'name' || field === 'phone')) {
          if (!orderLevelChanges[orderId]) {
            orderLevelChanges[orderId] = {};
          }
          orderLevelChanges[orderId][field] = value;
          return; // Skip item-level processing
        }

        // Handle item-level changes
        if (!changesByOrder[orderId]) {
          changesByOrder[orderId] = {};
        }
        if (!changesByOrder[orderId][itemIndex]) {
          changesByOrder[orderId][itemIndex] = {
            needsAlteration: undefined,
            alterationDetails: {}
          };
        }

        if (field && value !== undefined) {
          if (field === 'needsAlteration') {
            // Handle needsAlteration field
            changesByOrder[orderId][itemIndex].needsAlteration = value;
          } else {
            // Handle alterationDetails fields
            const actualField = field.replace('alterationDetails.', '');
            changesByOrder[orderId][itemIndex].alterationDetails[actualField] = value;
          }
        } else if (newStatus !== undefined) {
          // Legacy support for old format
          changesByOrder[orderId][itemIndex].needsAlteration = newStatus;
        }
      });

      // Process order-level changes first (SMS status is stored on Customer only, not on order)
      for (const orderId of Object.keys(orderLevelChanges)) {
        const orderRef = doc(db, "Orders", orderId);
        const orderFields = orderLevelChanges[orderId];
        const { smsSubscribed, ...orderUpdateFields } = orderFields;
        const updateData = orderUpdateFields;

        const currentOrder = await resolveOrderForSave(orderId);

        const effectivePhoneForSms = (orderUpdateFields.phone ?? currentOrder?.phone ?? '').toString();
        let normalizedPhoneForSms = '';
        if (effectivePhoneForSms) {
          const d = effectivePhoneForSms.replace(/\D/g, '');
          normalizedPhoneForSms = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
        }
        if (smsSubscribed !== undefined && normalizedPhoneForSms.length === 10) {
          const customerRef = doc(db, "Customers", normalizedPhoneForSms);
          setDoc(customerRef, { smsSubscribed }, { merge: true });
          if (smsSubscribed === true) {
            const optOutRef = doc(db, "SMSOptOuts", normalizedPhoneForSms);
            setDoc(optOutRef, {
              phoneNumber: `+1${normalizedPhoneForSms}`,
              optedOut: false,
              updatedAt: new Date().toISOString()
            }, { merge: true });
          }
        }

        if (Object.keys(updateData).length > 0) {
          // When status changes, append to statusLog so it stays in sync with active alterations
          if (updateData.status !== undefined) {
            const statusLogEntry = {
              userId: user?.uid,
              userName: user?.displayName || user?.email || 'Admin',
              status: updateData.status,
              timestamp: Timestamp.now()
            };
            await updateDoc(orderRef, {
              ...updateData,
              statusLog: arrayUnion(statusLogEntry)
            });
          } else {
            await setDoc(orderRef, updateData, { merge: true });
          }

          // Link / update Customers doc when name or phone was set on the order
          if (updateData.name !== undefined || updateData.phone !== undefined) {
            const phoneRaw = (updateData.phone ?? currentOrder?.phone ?? '').toString();
            let digits = phoneRaw.replace(/\D/g, '');
            if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
            const mergedName = (updateData.name ?? currentOrder?.name ?? '').toString().trim();
            if (digits.length === 10 && mergedName && mergedName.toLowerCase() !== 'undefined undefined') {
              const customerRef = doc(db, 'Customers', digits);
              const customerSnap = await getDoc(customerRef);
              const orderSnap = await getDoc(orderRef);
              const orderData = orderSnap.data() || {};
              const line_items = orderData.line_items || [];
              const ts = Timestamp.now();
              if (customerSnap.exists()) {
                const customerData = customerSnap.data();
                const existingOrders = customerData.orders || [];
                const orderExists = existingOrders.some((o) => o.orderNumber === orderId);
                const newOrderEntry = {
                  orderNumber: orderId,
                  timestamp: ts,
                  line_items
                };
                const nextOrders = orderExists ? existingOrders : [...existingOrders, newOrderEntry];
                await setDoc(
                  customerRef,
                  {
                    name: mergedName,
                    phone: digits,
                    smsSubscribed: true,
                    orders: nextOrders,
                    lastOrderDate: ts,
                    totalOrders: nextOrders.length
                  },
                  { merge: true }
                );
              } else {
                await setDoc(customerRef, {
                  name: mergedName,
                  phone: digits,
                  email: '',
                  smsSubscribed: true,
                  orders: [
                    {
                      orderNumber: orderId,
                      timestamp: ts,
                      line_items
                    }
                  ],
                  lastOrderDate: ts,
                  totalOrders: 1,
                  createdAt: ts
                });
              }
            }
          }
        }
      }

      // Process item-level changes
      for (const orderId of Object.keys(changesByOrder)) {
        const orderRef = doc(db, "Orders", orderId);

        const currentOrder = await resolveOrderForSave(orderId);
        if (!currentOrder) {
          console.error("Cannot save line item changes — order not found:", orderId);
          continue;
        }

        const updatedLineItems = [...(currentOrder.line_items || [])];

        Object.keys(changesByOrder[orderId]).forEach(itemIndex => {
          const itemChanges = changesByOrder[orderId][itemIndex];
          const itemIndexNum = parseInt(itemIndex);

          if (updatedLineItems[itemIndexNum]) {
            if (itemChanges.needsAlteration !== undefined) {
              updatedLineItems[itemIndexNum].needsAlteration = itemChanges.needsAlteration;
              
              // If unselecting alteration, clear alterationDetails
              if (itemChanges.needsAlteration === false) {
                updatedLineItems[itemIndexNum].alterationDetails = {};
              }
            }

            if (Object.keys(itemChanges.alterationDetails).length > 0) {
              updatedLineItems[itemIndexNum].alterationDetails = {
                ...updatedLineItems[itemIndexNum].alterationDetails,
                ...itemChanges.alterationDetails
              };
            }
          }
        });

        const updateData = {
          line_items: updatedLineItems
        };

        await setDoc(orderRef, updateData, { merge: true });
      }

      const applyLocalOrderUpdates = (order) => {
        const orderChanges = changesByOrder[order.id];
        const orderLevelFields = orderLevelChanges[order.id];
        let next = order;

        if (orderLevelFields) {
          next = { ...next, ...orderLevelFields };
        }

        if (!orderChanges) return next;

        return {
          ...next,
          line_items: (next.line_items || []).map((item, index) => {
            const itemChanges = orderChanges[index];
            if (!itemChanges) return item;

            let updatedItem = { ...item };

            if (itemChanges.needsAlteration !== undefined) {
              updatedItem.needsAlteration = itemChanges.needsAlteration;
            }

            if (Object.keys(itemChanges.alterationDetails).length > 0) {
              updatedItem.alterationDetails = {
                ...updatedItem.alterationDetails,
                ...itemChanges.alterationDetails
              };
            }

            return updatedItem;
          })
        };
      };

      setOrders(prev => prev.map(applyLocalOrderUpdates));
      setAllOrders(prev => prev.map(applyLocalOrderUpdates));
      setSearchResults(prev => prev.map(applyLocalOrderUpdates));
      
      // Update selectedOrder and originalOrder to reflect the saved state
      if (selectedOrder) {
        // Update selectedOrder with saved changes
        let updatedSelected = { ...selectedOrder };
        
        // Apply order-level changes
        if (orderLevelChanges[selectedOrder.id]) {
          updatedSelected = { ...updatedSelected, ...orderLevelChanges[selectedOrder.id] };
        }
        
        // Apply item-level changes
        const orderChanges = changesByOrder[selectedOrder.id];
        if (orderChanges) {
          updatedSelected.line_items = updatedSelected.line_items.map((item, index) => {
            const itemChanges = orderChanges[index];
            if (!itemChanges) return item;
            
            let updatedItem = { ...item };
            if (itemChanges.needsAlteration !== undefined) {
              updatedItem.needsAlteration = itemChanges.needsAlteration;
            }
            if (Object.keys(itemChanges.alterationDetails).length > 0) {
              updatedItem.alterationDetails = {
                ...updatedItem.alterationDetails,
                ...itemChanges.alterationDetails
              };
            }
            return updatedItem;
          });
        }
        
        setSelectedOrder(updatedSelected);
        // Update originalOrder to match the saved state (for future comparisons)
        setOriginalOrder(JSON.parse(JSON.stringify(updatedSelected)));
      }

      const savedCount = Object.keys(pendingChanges).length;
      setPendingChanges({});

      alert(`✅ Successfully saved ${savedCount} change${savedCount !== 1 ? 's' : ''}!`);

    } catch (error) {
      console.error("Error saving alteration changes:", error);
      alert("Failed to save some changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const openOrderModal = async (order) => {
    // Store a deep copy of the original order from database for comparison
    let orderToShow = { ...order };
    const originalOrderCopy = JSON.parse(JSON.stringify(order));

    // Use current customer SMS status: if customer is opted out (sms off), show not opted in even if order was placed when opted in
    let phone = (order.phone || '').toString().trim();
    if (phone) {
      let normalized = (phone || '').replace(/\D/g, '');
      if (normalized.length === 11 && normalized.startsWith('1')) normalized = normalized.slice(1);
      if (normalized) {
      try {
        const [customerSnap, optOutSnap] = await Promise.all([
          getDoc(doc(db, "Customers", normalized)),
          getDoc(doc(db, "SMSOptOuts", normalized)),
        ]);
        const customerData = customerSnap.exists() ? customerSnap.data() : null;
        const optedOut = optOutSnap.exists() && optOutSnap.data().optedOut === true;
        // If customer is opted out (sms off), always show not opted in; otherwise use customer's smsSubscribed or order value
        const displaySmsSubscribed = optedOut ? false : (customerData?.smsSubscribed === true);
        orderToShow = { ...orderToShow, smsSubscribed: displaySmsSubscribed };
        originalOrderCopy.smsSubscribed = displaySmsSubscribed;
      } catch (e) {
        console.warn("Could not load customer SMS status:", e);
      }
      }
    }

    setOriginalOrder(originalOrderCopy);
    setSelectedOrder(orderToShow);
    setStatusLogExpanded(false);
    setShowAddCustomerPanel(false);
    setAddCustomerNameDraft('');
    setAddCustomerPhoneDraft('');
    setShowModal(true);
    setPendingChanges({});
  };

  const closeOrderModal = () => {
    addCustomerPhoneLookupSeq.current += 1;
    setSelectedOrder(null);
    setOriginalOrder(null);
    setShowModal(false);
    setPendingChanges({});
    setShowAddCustomerPanel(false);
    setAddCustomerNameDraft('');
    setAddCustomerPhoneDraft('');
  };

  const openOrderModalRef = useRef(openOrderModal);
  openOrderModalRef.current = openOrderModal;

  useEffect(() => {
    const orderId = searchParams.get("order");
    if (!orderId) {
      lastOrderUrlParamRef.current = null;
      return;
    }
    if (!user || authLoading || loading) return;
    if (lastOrderUrlParamRef.current === orderId) return;

    lastOrderUrlParamRef.current = orderId;
    let cancelled = false;

    const clearOrderParam = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("order");
          return next;
        },
        { replace: true }
      );
    };

    (async () => {
      try {
        let order = allOrders.find((o) => o.id === orderId);
        if (!order) {
          try {
            const snap = await getDoc(doc(db, "Orders", orderId));
            if (snap.exists()) {
              order = { id: snap.id, ...snap.data() };
            }
          } catch (e) {
            console.warn("Could not load order from link:", e);
          }
        }
        if (cancelled) return;
        if (!order) {
          clearOrderParam();
          return;
        }
        await openOrderModalRef.current(order);
        clearOrderParam();
      } finally {
        if (!cancelled) {
          lastOrderUrlParamRef.current = null;
        }
      }
    })();

    return () => {
      cancelled = true;
      lastOrderUrlParamRef.current = null;
    };
  }, [searchParams, user, authLoading, loading, allOrders, setSearchParams]);

  const goToTailorOrderView = () => {
    if (!selectedOrder?.id) return;
    navigate(`/user?order=${encodeURIComponent(selectedOrder.id)}`);
  };

  const goToActiveAlterationsForOrder = () => {
    if (!selectedOrder?.id) return;
    navigate(
      `/admin/active-alterations?order=${encodeURIComponent(selectedOrder.id)}`
    );
  };

  const cancelAllAlterationsForSelectedOrder = async () => {
    if (!selectedOrder || !Array.isArray(selectedOrder.line_items)) return;
    const hasAnySelected = selectedOrder.line_items.some((item) => item?.needsAlteration === true);
    if (!hasAnySelected) {
      alert('This order has no selected alterations to cancel.');
      return;
    }

    const confirmed = window.confirm(
      'Cancel all alterations for this order? This will unselect every alteration and clear all alteration details.'
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const clearedLineItems = selectedOrder.line_items.map((item) => ({
        ...item,
        needsAlteration: false,
        alterationDetails: {}
      }));

      const orderRef = doc(db, 'Orders', selectedOrder.id);
      await setDoc(orderRef, { line_items: clearedLineItems }, { merge: true });

      setSelectedOrder((prev) => (prev ? { ...prev, line_items: clearedLineItems } : prev));
      setOriginalOrder((prev) => (prev ? { ...prev, line_items: clearedLineItems } : prev));
      setOrders((prev) => prev.map((o) => (o.id === selectedOrder.id ? { ...o, line_items: clearedLineItems } : o)));
      setAllOrders((prev) => prev.map((o) => (o.id === selectedOrder.id ? { ...o, line_items: clearedLineItems } : o)));

      setPendingChanges((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (next[key]?.orderId === selectedOrder.id) {
            delete next[key];
          }
        });
        return next;
      });

      alert('All alterations were canceled and saved for this order.');
    } catch (error) {
      console.error('Error canceling all alterations:', error);
      alert('Failed to cancel all alterations. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const tryFillAddCustomerNameFromFirestore = async (digits10) => {
    if (!digits10 || digits10.length !== 10) return;
    const seq = ++addCustomerPhoneLookupSeq.current;
    try {
      const snap = await getDoc(doc(db, 'Customers', digits10));
      if (seq !== addCustomerPhoneLookupSeq.current) return;
      if (!snap.exists()) return;
      const nm = (snap.data()?.name || '').toString().trim();
      if (nm) setAddCustomerNameDraft(nm);
    } catch (e) {
      console.warn('Could not look up customer by phone:', e);
    }
  };

  const handleAddCustomerPhoneDraftChange = (e) => {
    const raw = e.target.value;
    setAddCustomerPhoneDraft(raw);
    const digits = normalizeUsPhoneDigits(raw);
    if (digits.length === 10) {
      void tryFillAddCustomerNameFromFirestore(digits);
    }
  };

  const openAddCustomerPanel = () => {
    if (!selectedOrder) return;
    const n = (selectedOrder.name || '').toString().trim();
    const bad =
      !n ||
      n.toLowerCase() === 'undefined undefined' ||
      n.toLowerCase() === 'undefined';
    const norm = normalizeUsPhoneDigits(selectedOrder.phone);
    const phoneDraft = norm.length === 10 ? norm : '';
    setAddCustomerPhoneDraft(phoneDraft);
    setAddCustomerNameDraft(bad ? '' : String(selectedOrder.name || '').trim());
    setShowAddCustomerPanel(true);
    if (phoneDraft.length === 10) {
      void tryFillAddCustomerNameFromFirestore(phoneDraft);
    }
  };

  const applyAddCustomerToOrder = () => {
    if (!selectedOrder) return;
    const name = addCustomerNameDraft.trim();
    let digits = normalizeUsPhoneDigits(addCustomerPhoneDraft);
    if (!name) {
      alert('Please enter the customer name.');
      return;
    }
    if (digits.length !== 10) {
      alert('Please enter a valid 10-digit US phone number.');
      return;
    }
    setSelectedOrder((prev) => ({ ...prev, name, phone: digits }));
    setPendingChanges((prev) => ({
      ...prev,
      [`${selectedOrder.id}_name`]: {
        orderId: selectedOrder.id,
        itemIndex: 'order',
        field: 'name',
        value: name
      },
      [`${selectedOrder.id}_phone`]: {
        orderId: selectedOrder.id,
        itemIndex: 'order',
        field: 'phone',
        value: digits
      }
    }));
    addCustomerPhoneLookupSeq.current += 1;
    setShowAddCustomerPanel(false);
    setAddCustomerNameDraft('');
    setAddCustomerPhoneDraft('');
  };

  // Generate time options from 10am to 8pm at 30-minute intervals
  const generateTimeOptions = () => {
    const options = [];
    // Start at 10:00 AM (10:00 in 24-hour format)
    for (let hour = 10; hour <= 20; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        // Stop at 8:00 PM (20:00)
        if (hour === 20 && minute > 0) break;
        
        const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        
        // Convert to 12-hour format for display
        let displayHour = hour;
        let ampm = 'AM';
        if (hour === 12) {
          ampm = 'PM';
        } else if (hour > 12) {
          displayHour = hour - 12;
          ampm = 'PM';
        }
        
        const displayTime = `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
        options.push({ value: time24, label: displayTime });
      }
    }
    return options;
  };

  const navigateToCustomerProfile = () => {
    if (selectedOrder && selectedOrder.phone) {
      // Navigate to customers page with the phone number as search parameter
      navigate(`/admin/customers?search=${selectedOrder.phone}`);
    }
  };

  // Helper function to check if an order has any alterations
  const hasAlterations = (order) => {
    if (!order.line_items || !Array.isArray(order.line_items)) {
      return false;
    }
    return order.line_items.some(item => item.needsAlteration === true);
  };

  // Helper function to check if an order has active alterations (has alterationDetails)
  const hasActiveAlterations = (order) => {
    if (!order.line_items || !Array.isArray(order.line_items)) {
      return false;
    }
    return order.line_items.some(item => 
      item.needsAlteration === true && 
      item.alterationDetails && 
      Object.keys(item.alterationDetails).length > 0
    );
  };

  if (authLoading) {
    return (
      <div className="admin-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="admin-container">
        <div className="auth-error">
          <h2>Access Denied</h2>
          <p>You must be logged in to access the Admin Dashboard.</p>
          <p>Please log in and try again.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-container">
        <div className="orders-container">
          <div className="error-message">
            {error}
          </div>
          <div className="debug-buttons">
            <button
              className="fetch-orders-btn"
              onClick={refreshOrders}
            >
              Retry
            </button>
            <button
              className="test-fetch-btn"
              onClick={testFetchOrders}
            >
              🧪 Test Fetch
            </button>
            <button
              className="test-access-btn"
              onClick={testCollectionAccess}
            >
              🔍 Test Access
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div className="admin-header-content">
          <h1>Admin Dashboard</h1>
          <div className="search-container">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search by order ID, barcode ID, receipt number or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchingAll && (
              <span className="search-loading-spinner" aria-label="Searching orders" />
            )}
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className={`clear-search-btn${searchingAll ? ' with-spinner' : ''}`}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>
        {searchTerm && (
          <div className="search-results-info">
            {searchingAll && (
              <span className="search-results-spinner" aria-hidden="true" />
            )}
            <span>
              {searchingAll
                ? `Searching all orders… ${matchingOrders.length} match${matchingOrders.length !== 1 ? 'es' : ''} so far`
                : `${matchingOrders.length} match${matchingOrders.length !== 1 ? 'es' : ''} across all orders`}
            </span>
          </div>
        )}
      </div>

      <div className="orders-container">

        {filteredOrders.length === 0 ? (
          <div className="no-orders">
            <h3>{searchTerm ? 'No orders match your search' : 'No orders found'}</h3>
            <p>
              {searchTerm
                ? (searchingAll
                  ? 'Searching every order for this name, receipt number, or ID…'
                  : 'Try a different order ID, barcode ID, receipt number or name')
                : 'There are no orders in the system yet.'}
            </p>
          </div>
        ) : (
          <div className="orders-list">
            {filteredOrders.map((order) => {
              const isPickedUp = order.status === 'picked up';
              const hasActive = hasActiveAlterations(order);
              return (
              <div
                key={order.id}
                className={`order-card ${hasAlterations(order) ? 'has-alterations' : ''} ${isPickedUp ? 'picked-up' : ''}`}
                onClick={() => openOrderModal(order)}
              >
                {isPickedUp ? (
                  <div className="alteration-indicator-dot picked-up-dot"></div>
                ) : hasAlterations(order) && (
                  <div className={`alteration-indicator-dot ${hasActive ? 'active' : ''}`}></div>
                )}
                <div className="order-info">
                  <div className="order-id">
                    <span className="order-label">Order #</span>
                    <span className="order-value">{order.id}</span>
                  </div>
                  <div className="order-receipt">
                    <span className="order-label">Receipt #</span>
                    <span className="order-value">{order.receiptNumber || 'N/A'}</span>
                  </div>
                  <div className="order-customer">
                    <span className="order-label">Customer</span>
                    <span className="order-value order-customer-name">{order.name}</span>
                  </div>
                  <div className="order-date">
                    <span className="order-label">Date</span>
                    <span className="order-value">
                      {formatDateOnly(order.timestamp)}
                    </span>
                  </div>
                </div>
                <div className="order-actions">
                  <button 
                    className="order-customer-profile-btn" 
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/customers?search=${order.phone}`);
                    }}
                    title="View Customer Profile"
                  >
                    <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </button>
                  <div className="order-arrow">→</div>
                </div>
              </div>
            );
            })}
          </div>
        )}

        <div className="load-more-container">
          <div className="pagination-controls">
            <button
              className="nav-btn nav-btn-first"
              onClick={goToFirstPage}
              disabled={!hasPreviousOrders || loadingMore}
            >
              ⏮ First
            </button>

            <button
              className="nav-btn nav-btn-prev"
              onClick={goToPreviousPage}
              disabled={!hasPreviousOrders || loadingMore}
            >
              ← Previous 10
            </button>

            <div className="page-info">
              Page {currentPage + 1} of {loadedPages}{!isSearching && hasMoreOrders ? '+' : ''}
              {loadingMore || searchingAll ? ' · Loading…' : ''}
            </div>

            <button
              className="nav-btn nav-btn-next"
              onClick={goToNextPage}
              disabled={!canGoNextPage || loadingMore}
            >
              {loadingMore ? 'Loading…' : 'Next 10 →'}
            </button>
          </div>

          
        </div>
      </div>

      {/* Order Details Modal */}
      {showModal && selectedOrder && (
        <div className="modal-overlay" onClick={closeOrderModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details</h2>
              <div className="modal-header-actions">
                {hasActiveAlterations(selectedOrder) && (
                  <button
                    type="button"
                    className="jump-to-alterations-btn"
                    onClick={goToActiveAlterationsForOrder}
                    title="Open active alterations and this order"
                  >
                    Alterations
                  </button>
                )}
                {hasAlterations(selectedOrder) && (
                  <button
                    type="button"
                    className="cancel-alterations-header-btn"
                    onClick={cancelAllAlterationsForSelectedOrder}
                    title="Clear and unselect all alterations for this order"
                    disabled={saving}
                  >
                    {saving ? 'Cancelling...' : 'Cancel Alterations'}
                  </button>
                )}
                {orderNeedsCustomerData(selectedOrder) && (
                  <button
                    type="button"
                    className="add-customer-header-btn"
                    onClick={openAddCustomerPanel}
                    title="Add customer name and phone to this order"
                  >
                    Add customer
                  </button>
                )}
                {claims?.user === true && (
                  <button
                    type="button"
                    className="cross-dashboard-order-btn"
                    onClick={goToTailorOrderView}
                    title="Open this order in the tailor (orders) dashboard"
                  >
                    Tailor view
                  </button>
                )}
                <button 
                  className="customer-profile-btn" 
                  onClick={navigateToCustomerProfile}
                  title="View Customer Profile"
                >
                  <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  Customer Profile
                </button>
                <button className="modal-close" onClick={closeOrderModal}>
                  ×
                </button>
              </div>
            </div>

            {showAddCustomerPanel && (
              <div className="add-customer-panel" role="region" aria-label="Add customer to order">
                <p className="add-customer-panel-intro">
                  Enter phone first — if this number is already in Customers, the name will fill in automatically. Then click Apply and use <strong>Save changes</strong> to write to the database.
                </p>
                <div className="add-customer-panel-fields">
                  <label>
                    Phone (10 digits)
                    <input
                      type="tel"
                      value={addCustomerPhoneDraft}
                      onChange={handleAddCustomerPhoneDraftChange}
                      placeholder="5551234567"
                      inputMode="numeric"
                      autoComplete="tel"
                    />
                  </label>
                  <label>
                    Customer name
                    <input
                      type="text"
                      value={addCustomerNameDraft}
                      onChange={(e) => setAddCustomerNameDraft(e.target.value)}
                      placeholder="First Last"
                      autoComplete="name"
                    />
                  </label>
                </div>
                <div className="add-customer-panel-actions">
                  <button type="button" className="add-customer-apply-btn" onClick={applyAddCustomerToOrder}>
                    Apply to order
                  </button>
                  <button
                    type="button"
                    className="add-customer-cancel-btn"
                    onClick={() => {
                      addCustomerPhoneLookupSeq.current += 1;
                      setShowAddCustomerPanel(false);
                      setAddCustomerNameDraft('');
                      setAddCustomerPhoneDraft('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="modal-body">
              <div className="order-detail-section">
                <h3>Order Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Order ID:</span>
                    <span className="detail-value">{selectedOrder.id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Receipt Number:</span>
                    <span className="detail-value">{selectedOrder.receiptNumber || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Customer Name:</span>
                    <span className="detail-value">{selectedOrder.name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{formatPhoneNumber(selectedOrder.phone)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Date Placed:</span>
                    <span className="detail-value">
                      {formatTimestamp(selectedOrder.timestamp)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">SMS Subscribed:</span>
                    <label className="sms-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedOrder.smsSubscribed || false}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setSelectedOrder(prev => ({
                            ...prev,
                            smsSubscribed: newValue
                          }));
                          // Add to pending changes
                          const changeKey = `${selectedOrder.id}_smsSubscribed`;
                          setPendingChanges(prev => ({
                            ...prev,
                            [changeKey]: {
                              orderId: selectedOrder.id,
                              itemIndex: 'order',
                              field: 'smsSubscribed',
                              value: newValue
                            }
                          }));
                        }}
                        className="sms-subscription-checkbox"
                      />
                      <span>Customer has opted in to receive SMS notifications</span>
                    </label>
                  </div>
                </div>
                
                {/* Order-level Due Date, Time, and Priority */}
                <div className="order-controls">
                  <div className="order-control-field">
                    <label>Due Date:</label>
                    <input
                      type="date"
                      value={selectedOrder.dueDate || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setSelectedOrder(prev => ({
                          ...prev,
                          dueDate: newValue
                        }));
                        const changeKey = `${selectedOrder.id}_dueDate`;
                        setPendingChanges(prev => ({
                          ...prev,
                          [changeKey]: {
                            orderId: selectedOrder.id,
                            itemIndex: 'order',
                            field: 'dueDate',
                            value: newValue
                          }
                        }));
                      }}
                    />
                  </div>
                  
                  <div className="order-control-field">
                    <label>Due Time:</label>
                    <select
                      value={selectedOrder.dueTime || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setSelectedOrder(prev => ({
                          ...prev,
                          dueTime: newValue
                        }));
                        const changeKey = `${selectedOrder.id}_dueTime`;
                        setPendingChanges(prev => ({
                          ...prev,
                          [changeKey]: {
                            orderId: selectedOrder.id,
                            itemIndex: 'order',
                            field: 'dueTime',
                            value: newValue
                          }
                        }));
                      }}
                    >
                      <option value="">Select time</option>
                      {generateTimeOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="order-control-field">
                    <label>Priority:</label>
                    <select
                      value={selectedOrder.priority || 'none'}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setSelectedOrder(prev => ({
                          ...prev,
                          priority: newValue
                        }));
                        const changeKey = `${selectedOrder.id}_priority`;
                        setPendingChanges(prev => ({
                          ...prev,
                          [changeKey]: {
                            orderId: selectedOrder.id,
                            itemIndex: 'order',
                            field: 'priority',
                            value: newValue
                          }
                        }));
                      }}
                      className={`priority-select priority-${selectedOrder.priority || 'none'}`}
                    >
                      <option value="none">None</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>

                  <div className="order-control-field">
                    <label>Status:</label>
                    <select
                      value={selectedOrder.status || 'order created'}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setSelectedOrder(prev => ({
                          ...prev,
                          status: newValue
                        }));
                        const changeKey = `${selectedOrder.id}_status`;
                        setPendingChanges(prev => ({
                          ...prev,
                          [changeKey]: {
                            orderId: selectedOrder.id,
                            itemIndex: 'order',
                            field: 'status',
                            value: newValue
                          }
                        }));
                      }}
                      className={`status-select status-${(selectedOrder.status || 'order created').replace(/\s+/g, '-')}`}
                    >
                      <option value="order created">Order Created</option>
                      <option value="starting alterations">Starting Alterations</option>
                      <option value="ready for pick up">Ready for Pick Up</option>
                      <option value="picked up">Picked Up</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className={`order-detail-section status-log-section ${statusLogExpanded ? 'expanded' : ''}`}>
                <button
                  type="button"
                  className="status-log-section-toggle"
                  onClick={() => setStatusLogExpanded(prev => !prev)}
                  aria-expanded={statusLogExpanded}
                >
                  <h3>
                    Status Change Log
                    <span className="status-log-count"> ({selectedOrder.statusLog?.length ?? 0})</span>
                  </h3>
                  <span className="status-log-toggle-icon" aria-hidden>{statusLogExpanded ? '▼' : '▶'}</span>
                </button>
                {statusLogExpanded && (
                  <>
                    {selectedOrder.statusLog && selectedOrder.statusLog.length > 0 ? (
                      <div className="status-log-list">
                        {[...selectedOrder.statusLog]
                          .sort((a, b) => {
                            const aTime = a?.timestamp?.toDate
                              ? a.timestamp.toDate().getTime()
                              : (typeof a?.timestamp === 'object' && typeof a?.timestamp?.seconds === 'number'
                                  ? a.timestamp.seconds * 1000
                                  : new Date(a?.timestamp || 0).getTime());
                            const bTime = b?.timestamp?.toDate
                              ? b.timestamp.toDate().getTime()
                              : (typeof b?.timestamp === 'object' && typeof b?.timestamp?.seconds === 'number'
                                  ? b.timestamp.seconds * 1000
                                  : new Date(b?.timestamp || 0).getTime());
                            return bTime - aTime;
                          })
                          .map((entry, index) => (
                            <div className="status-log-entry" key={`${entry.status || 'status'}-${index}`}>
                              <div className="status-log-status">
                                {formatStatusLabel(entry.status)}
                              </div>
                              <div className="status-log-meta">
                                <span>{entry.userName || 'Unknown User'}</span>
                                <span>{formatStatusLogTimestamp(entry.timestamp)}</span>
                                {entry.sms?.status && (
                                  <span className="status-log-sms-status">
                                    SMS: {formatSmsStatus(entry.sms.status)}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="status-log-empty">No status updates recorded yet.</p>
                    )}
                  </>
                )}
              </div>

              <div className="order-detail-section">
                <h3>Line Items</h3>
                {selectedOrder.line_items && Array.isArray(selectedOrder.line_items) && selectedOrder.line_items.length > 0 ? (
                  <div className="line-items-list">
                    {selectedOrder.line_items.map((item, index) => {
                      const jacketAlterationCount = countSelectedAlterationsInSection(item.alterationDetails, 'Jacket');
                      const pantsAlterationCount = countSelectedAlterationsInSection(item.alterationDetails, 'Pants');
                      const shirtAlterationCount = countSelectedAlterationsInSection(item.alterationDetails, 'Shirt');
                      const vestAlterationCount = countSelectedAlterationsInSection(item.alterationDetails, 'Vest');
                      return (
                      <div key={index} className="line-item">
                        <div className="item-main-row">
                          <div className="item-details">
                            <span className="item-name">{item.name || 'Unnamed Item'}</span>
                            <span className="item-quantity">Qty: {item.quantity || 1}</span>
                          </div>
                          <div className="item-actions">
                            <button
                              className={`alteration-checkbox ${(item.needsAlteration || false) ? 'checked' : ''}`}
                              onClick={() => handleAlterationToggle(selectedOrder.id, index, item.needsAlteration || false)}
                              title={(item.needsAlteration || false) ? 'Mark as no alteration needed' : 'Mark for alteration'}
                            >
                              {(item.needsAlteration || false) ? '✓' : ''}
                            </button>
                            <span className="alteration-label">Alteration</span>
                            {item.price && (
                              <span className="item-price">
                                ${(item.price * (item.quantity || 1)).toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Alteration Details Dropdown */}
                        {(item.needsAlteration || false) && (
                          <div className="alteration-details-dropdown">
                            <div className="alteration-header">
                              <button
                                className="toggle-details-btn"
                                onClick={() => toggleAlterationDetails(index)}
                              >
                                {expandedItems[index] ? '▼' : '▶'} Alteration Details
                              </button>
                              <button
                                className={`use-measurements-btn ${item.alterationDetails?.useCustomerMeasurements ? 'active' : ''}`}
                                onClick={() => updateAlterationDetail(index, 'useCustomerMeasurements', !item.alterationDetails?.useCustomerMeasurements)}
                                title="Mark to use customer's saved measurements for this item"
                              >
                                Use Customer's Saved Measurements
                              </button>
                            </div>

                            {expandedItems[index] && (
                              <div className="alteration-details-content">
                                <div className="alteration-sections">
                                  {/* Jacket Section */}
                                  <div className="alteration-section">
                                    <button
                                      type="button"
                                      className="section-title section-title-toggle"
                                      onClick={() => toggleAlterationSection(index, 'Jacket')}
                                    >
                                      <span className="section-title-start">
                                        <span className="section-title-text">Jacket</span>
                                        <span
                                          className="section-title-count"
                                          aria-label={`${jacketAlterationCount} selected alterations in this section`}
                                        >
                                          {jacketAlterationCount}
                                        </span>
                                      </span>
                                      <span className="section-title-arrow">
                                        {isAlterationSectionExpanded(index, 'Jacket') ? '▼' : '▶'}
                                      </span>
                                    </button>
                                    {isAlterationSectionExpanded(index, 'Jacket') && (
                                    <>
                                    <div className="alteration-notes-row">
                                      <label htmlFor={`jacket-notes-${index}`} className="alteration-notes-label">Jacket Notes</label>
                                      <textarea
                                        id={`jacket-notes-${index}`}
                                        className="alteration-notes-input"
                                        value={item.alterationDetails?.jacketNotes || ''}
                                        onChange={(e) => updateAlterationDetail(index, 'jacketNotes', e.target.value)}
                                        placeholder="Add jacket-specific notes..."
                                        rows={2}
                                      />
                                    </div>
                                    <div className="section-buttons">
                                      {/* 1. Sleeve Longer */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.sleeveLonger ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'sleeveLonger', !item.alterationDetails?.sleeveLonger)}
                                        >
                                          Sleeve Longer
                                        </button>
                                        {item.alterationDetails?.sleeveLonger && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.sleeveLongerValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'sleeveLongerValue', e.target.value)}
                                            placeholder="Enter number"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 2. Sleeve Shorter */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.sleeveShorter ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'sleeveShorter', !item.alterationDetails?.sleeveShorter)}
                                        >
                                          Sleeve Shorter
                                        </button>
                                        {item.alterationDetails?.sleeveShorter && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.sleeveShorterValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'sleeveShorterValue', e.target.value)}
                                            placeholder="Enter number"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 3. Take In */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.takeIn ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'takeIn', !item.alterationDetails?.takeIn)}
                                        >
                                          Take In
                                        </button>
                                        {item.alterationDetails?.takeIn && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.takeInValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'takeInValue', e.target.value)}
                                            placeholder="Enter number"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 4. Take Out */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.takeOut ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'takeOut', !item.alterationDetails?.takeOut)}
                                        >
                                          Take Out
                                        </button>
                                        {item.alterationDetails?.takeOut && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.takeOutValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'takeOutValue', e.target.value)}
                                            placeholder="Enter number"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 5. Shorter Jacket */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.shorterJacket ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'shorterJacket', !item.alterationDetails?.shorterJacket)}
                                        >
                                          Shorter Jacket
                                        </button>
                                        {item.alterationDetails?.shorterJacket && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.shorterJacketValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'shorterJacketValue', e.target.value)}
                                            placeholder="Enter number"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 6. Shoulder Pads */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.shoulderPads ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'shoulderPads', !item.alterationDetails?.shoulderPads)}
                                        >
                                          Shoulder Pads
                                        </button>
                                      </div>

                                      {/* 7. Functional Button Hole - should be in second column */}
                                      <div className="alteration-button-group jacket-item-7">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.functionButtonHole ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'functionButtonHole', !item.alterationDetails?.functionButtonHole)}
                                        >
                                          Functional Button Hole
                                        </button>
                                      </div>

                                      {/* 8. Change Buttons */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.changeButtonsJacket ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'changeButtonsJacket', !item.alterationDetails?.changeButtonsJacket)}
                                        >
                                          Change Buttons
                                        </button>
                                      </div>

                                      {/* 9. Taper Sleeve */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.taperSleeve ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'taperSleeve', !item.alterationDetails?.taperSleeve)}
                                        >
                                          Taper Sleeve
                                        </button>
                                        {item.alterationDetails?.taperSleeve && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.taperSleeveValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'taperSleeveValue', e.target.value)}
                                            placeholder="Enter number"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 10. Shattnez Test */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.shattnezTest ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'shattnezTest', !item.alterationDetails?.shattnezTest)}
                                        >
                                          Shattnez Test
                                        </button>
                                      </div>

                                      {/* 11. Remove Shattnez */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.removeShattnez ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'removeShattnez', !item.alterationDetails?.removeShattnez)}
                                        >
                                          Remove Shattnez
                                        </button>
                                      </div>
                                    </div>
                                    </>
                                    )}
                                  </div>

                                  {/* Pants Section */}
                                  <div className="alteration-section">
                                    <button
                                      type="button"
                                      className="section-title section-title-toggle"
                                      onClick={() => toggleAlterationSection(index, 'Pants')}
                                    >
                                      <span className="section-title-start">
                                        <span className="section-title-text">Pants</span>
                                        <span
                                          className="section-title-count"
                                          aria-label={`${pantsAlterationCount} selected alterations in this section`}
                                        >
                                          {pantsAlterationCount}
                                        </span>
                                      </span>
                                      <span className="section-title-arrow">
                                        {isAlterationSectionExpanded(index, 'Pants') ? '▼' : '▶'}
                                      </span>
                                    </button>
                                    {isAlterationSectionExpanded(index, 'Pants') && (
                                    <>
                                    <div className="alteration-notes-row">
                                      <label htmlFor={`pants-notes-${index}`} className="alteration-notes-label">Pants Notes</label>
                                      <textarea
                                        id={`pants-notes-${index}`}
                                        className="alteration-notes-input"
                                        value={item.alterationDetails?.pantsNotes || ''}
                                        onChange={(e) => updateAlterationDetail(index, 'pantsNotes', e.target.value)}
                                        placeholder="Add pants-specific notes..."
                                        rows={2}
                                      />
                                    </div>
                                    <div className="section-buttons">
                                      {/* 1. Hemming */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.hemming ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'hemming', !item.alterationDetails?.hemming)}
                                        >
                                          Hemming
                                        </button>
                                        {item.alterationDetails?.hemming && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.hemmingValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'hemmingValue', e.target.value)}
                                            placeholder="Enter value"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 2. Taper Pants */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.taperPants ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'taperPants', !item.alterationDetails?.taperPants)}
                                        >
                                          Taper Pants
                                        </button>
                                        {item.alterationDetails?.taperPants && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.taperPantsValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'taperPantsValue', e.target.value)}
                                            placeholder="Enter value"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 3. Waist In */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.waistIn ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'waistIn', !item.alterationDetails?.waistIn)}
                                        >
                                          Waist In
                                        </button>
                                        {item.alterationDetails?.waistIn && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.waistInValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'waistInValue', e.target.value)}
                                            placeholder="Enter value"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 4. Waist Out */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.waistOut ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'waistOut', !item.alterationDetails?.waistOut)}
                                        >
                                          Waist Out
                                        </button>
                                        {item.alterationDetails?.waistOut && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.waistOutValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'waistOutValue', e.target.value)}
                                            placeholder="Enter value"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 5. Seat In */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.seatIn ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'seatIn', !item.alterationDetails?.seatIn)}
                                        >
                                          Seat In
                                        </button>
                                        {item.alterationDetails?.seatIn && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.seatInValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'seatInValue', e.target.value)}
                                            placeholder="Enter value"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 6. Seat Out */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.seatOut ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'seatOut', !item.alterationDetails?.seatOut)}
                                        >
                                          Seat Out
                                        </button>
                                        {item.alterationDetails?.seatOut && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.seatOutValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'seatOutValue', e.target.value)}
                                            placeholder="Enter value"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 7. Crotch In */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.crotchIn ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'crotchIn', !item.alterationDetails?.crotchIn)}
                                        >
                                          Crotch In
                                        </button>
                                        {item.alterationDetails?.crotchIn && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.crotchInValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'crotchInValue', e.target.value)}
                                            placeholder="Enter value"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 8. Crotch Out */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.crotchOut ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'crotchOut', !item.alterationDetails?.crotchOut)}
                                        >
                                          Crotch Out
                                        </button>
                                        {item.alterationDetails?.crotchOut && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.crotchOutValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'crotchOutValue', e.target.value)}
                                            placeholder="Enter value"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 9. Crotch Lining */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.crotchLining ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'crotchLining', !item.alterationDetails?.crotchLining)}
                                        >
                                          Crotch Lining
                                        </button>
                                      </div>

                                      {/* 10. Change Zipper */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.changeZipper ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'changeZipper', !item.alterationDetails?.changeZipper)}
                                        >
                                          Change Zipper
                                        </button>
                                      </div>

                                      {/* 11. Length - with 2 number inputs */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.length ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'length', !item.alterationDetails?.length)}
                                        >
                                          Length
                                        </button>
                                        {item.alterationDetails?.length && (
                                          <div className="length-inputs">
                                            <input
                                              type="number"
                                              className="alteration-input"
                                              value={item.alterationDetails?.lengthShorter || ''}
                                              onChange={(e) => updateAlterationDetail(index, 'lengthShorter', e.target.value)}
                                              placeholder="Shorter"
                                              min="0"
                                              step="1"
                                            />
                                            <input
                                              type="number"
                                              className="alteration-input"
                                              value={item.alterationDetails?.lengthLonger || ''}
                                              onChange={(e) => updateAlterationDetail(index, 'lengthLonger', e.target.value)}
                                              placeholder="Longer"
                                              min="0"
                                              step="1"
                                            />
                                          </div>
                                        )}
                                      </div>

                                      {/* 12. Change Buttons */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.changeButtons ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'changeButtons', !item.alterationDetails?.changeButtons)}
                                        >
                                          Change Buttons
                                        </button>
                                      </div>

                                      {/* 13. Suspender Buttons */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.suspenderButtons ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'suspenderButtons', !item.alterationDetails?.suspenderButtons)}
                                        >
                                          Suspender Buttons
                                        </button>
                                      </div>
                                    </div>
                                    </>
                                    )}
                                  </div>

                                  {/* Shirt Section */}
                                  <div className="alteration-section">
                                    <button
                                      type="button"
                                      className="section-title section-title-toggle"
                                      onClick={() => toggleAlterationSection(index, 'Shirt')}
                                    >
                                      <span className="section-title-start">
                                        <span className="section-title-text">Shirt</span>
                                        <span
                                          className="section-title-count"
                                          aria-label={`${shirtAlterationCount} selected alterations in this section`}
                                        >
                                          {shirtAlterationCount}
                                        </span>
                                      </span>
                                      <span className="section-title-arrow">
                                        {isAlterationSectionExpanded(index, 'Shirt') ? '▼' : '▶'}
                                      </span>
                                    </button>
                                    {isAlterationSectionExpanded(index, 'Shirt') && (
                                    <>
                                    <div className="alteration-notes-row">
                                      <label htmlFor={`shirt-notes-${index}`} className="alteration-notes-label">Shirt Notes</label>
                                      <textarea
                                        id={`shirt-notes-${index}`}
                                        className="alteration-notes-input"
                                        value={item.alterationDetails?.shirtNotes || ''}
                                        onChange={(e) => updateAlterationDetail(index, 'shirtNotes', e.target.value)}
                                        placeholder="Add shirt-specific notes..."
                                        rows={2}
                                      />
                                    </div>
                                    <div className="section-buttons">
                                      {/* 1. Shorter Sleeve */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.shorterSleeve ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'shorterSleeve', !item.alterationDetails?.shorterSleeve)}
                                        >
                                          Shorter Sleeve
                                        </button>
                                        {item.alterationDetails?.shorterSleeve && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.shorterSleeveValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'shorterSleeveValue', e.target.value)}
                                            placeholder="Enter number"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 2. Take In */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.takeInShirt ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'takeInShirt', !item.alterationDetails?.takeInShirt)}
                                        >
                                          Take In
                                        </button>
                                        {item.alterationDetails?.takeInShirt && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.takeInShirtValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'takeInShirtValue', e.target.value)}
                                            placeholder="Enter number"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 3. Take Out */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.takeOutShirt ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'takeOutShirt', !item.alterationDetails?.takeOutShirt)}
                                        >
                                          Take Out
                                        </button>
                                        {item.alterationDetails?.takeOutShirt && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.takeOutShirtValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'takeOutShirtValue', e.target.value)}
                                            placeholder="Enter number"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>

                                      {/* 4. Iron */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.iron ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'iron', !item.alterationDetails?.iron)}
                                        >
                                          Iron
                                        </button>
                                      </div>
                                    </div>
                                    </>
                                    )}
                                  </div>

                                  {/* Vest Section */}
                                  <div className="alteration-section">
                                    <button
                                      type="button"
                                      className="section-title section-title-toggle"
                                      onClick={() => toggleAlterationSection(index, 'Vest')}
                                    >
                                      <span className="section-title-start">
                                        <span className="section-title-text">Vest</span>
                                        <span
                                          className="section-title-count"
                                          aria-label={`${vestAlterationCount} selected alterations in this section`}
                                        >
                                          {vestAlterationCount}
                                        </span>
                                      </span>
                                      <span className="section-title-arrow">
                                        {isAlterationSectionExpanded(index, 'Vest') ? '▼' : '▶'}
                                      </span>
                                    </button>
                                    {isAlterationSectionExpanded(index, 'Vest') && (
                                    <>
                                    <div className="alteration-notes-row">
                                      <label htmlFor={`vest-notes-${index}`} className="alteration-notes-label">Vest Notes</label>
                                      <textarea
                                        id={`vest-notes-${index}`}
                                        className="alteration-notes-input"
                                        value={item.alterationDetails?.vestNotes || ''}
                                        onChange={(e) => updateAlterationDetail(index, 'vestNotes', e.target.value)}
                                        placeholder="Add vest-specific notes..."
                                        rows={2}
                                      />
                                    </div>
                                    <div className="section-buttons">
                                      {/* 1. Change Buttons */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.changeButtonsVest ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'changeButtonsVest', !item.alterationDetails?.changeButtonsVest)}
                                        >
                                          Change Buttons
                                        </button>
                                      </div>
                                      {/* 2. Take In */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.takeInVest ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'takeInVest', !item.alterationDetails?.takeInVest)}
                                        >
                                          Take In
                                        </button>
                                        {item.alterationDetails?.takeInVest && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.takeInVestValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'takeInVestValue', e.target.value)}
                                            placeholder="Enter number"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>
                                      {/* 3. Take Out */}
                                      <div className="alteration-button-group">
                                        <button
                                          className={`alteration-btn ${item.alterationDetails?.takeOutVest ? 'active' : ''}`}
                                          onClick={() => updateAlterationDetail(index, 'takeOutVest', !item.alterationDetails?.takeOutVest)}
                                        >
                                          Take Out
                                        </button>
                                        {item.alterationDetails?.takeOutVest && (
                                          <input
                                            type="number"
                                            className="alteration-input"
                                            value={item.alterationDetails?.takeOutVestValue || ''}
                                            onChange={(e) => updateAlterationDetail(index, 'takeOutVestValue', e.target.value)}
                                            placeholder="Enter number"
                                            min="0"
                                            step="1"
                                          />
                                        )}
                                      </div>
                                    </div>
                                    </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                    })}
                  </div>
                ) : (
                  <p className="no-items">No line items found</p>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <div className="modal-actions">
                {Object.keys(pendingChanges).length > 0 && (
                  <div className="pending-changes-info">
                    {Object.keys(pendingChanges).length} change(s) pending
                  </div>
                )}
                <div className="modal-buttons">
                  <button
                    className="modal-btn modal-btn-save"
                    onClick={saveAlterationChanges}
                    disabled={saving || Object.keys(pendingChanges).length === 0}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button className="modal-btn" onClick={closeOrderModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;